/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2018, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../lib/@waca/core-client/js/core-client/utils/ClassFactory',
	'./LifeCycleManager',
	'../../lib/@waca/dashboard-common/dist/utils/dnd/DnDManager',
	'../../lib/@waca/baglass/js/baglass/services/ServiceRegistry',
	'../../canvas/CanvasFactory',
	'./DashboardStringService',
	'../../features/FeatureLoader',
	'./InlineFeatures',
	'./DeprecatedInlineFeatures',
	'../../features/dashboard/contentFactory/api/impl/ContentFactory',
	'../../features/dashboard/contentProvider/api/impl/BaseContentProvider',
	'../../features/dashboard/contentProvider/api/impl/DashboardContentProvider',
	'../../features/dashboard/contentProvider/api/impl/PageContentProvider',
	'../../features/dashboard/transaction/api/impl/Transaction',
	'../../lib/@waca/core-client/js/core-client/utils/PerfUtils'
], function (
	ClassFactory,
	LifeCycleManager,
	DnDManager,
	ServiceRegistry,
	CanvasFactory,
	DashboardStringService,
	FeatureLoader,
	inlineFeatures,
	deprecatedInlineFeatures,
	ContentFactory,
	BaseContentProvider,
	DashboardContentProvider,
	PagerContentProvider,
	Transaction,
	PerfUtils
) {

	/**
	*	Helper class used to create the core dashboard services and services registry
	*/
	const CORE_PROFILE_SPEC = {
		'profiles': ['core']
	};
	class ServicesHelper {

		constructor(options) {
			this.glassContext = options.glassContext;
			this.bootStrapFeatures = options.bootStrapFeatures;
			this._settings = this.bootStrapFeatures['DashboardSettings'].getAPI();
			this.eventRouter = options.eventRouter;
			this.logger = options.logger;
			this.dashboardAPI = options.dashboardAPI;
			this.inlineFeatures = options.inlineFeatures || inlineFeatures;
			this.internalDashboardAPI = options.internalDashboardAPI;
		}


		_createCanvas(dashboardApi, boardModel, widgetRegistry, extensions) {
			const factory = new CanvasFactory({
				dashboardAPI: dashboardApi,
				extensions: extensions,
				glassContext: this.glassContext,
				eventRouter: this.eventRouter,
				logger: this.logger,
				widgetRegistry: widgetRegistry,
				featureLoader: this.featureLoader,
				boardModel: boardModel,
				settings: this._settings
			});
			return factory.createCanvas();

		}

		_registerCanvasFeature(canvasFactoryResponse) {
			// Register the canvas in the feature loader
			this.featureLoader.registerFeature('Canvas', canvasFactoryResponse.canvasImpl, CORE_PROFILE_SPEC, true);
			this.featureLoader.registerFeature('Canvas.internal', {
				getAPI: () => {
					return canvasFactoryResponse.internalCanvas;
				}
			}, CORE_PROFILE_SPEC, true);
		}


		preDashboardInitialize() {
			return this.featureLoader.getFeature('LifeCycle').invokeHandlers('pre:dashboard.initialize');
		}

		createServices() {
			const response = {};
			response.lifeCycleManager = new LifeCycleManager();
			//do not instantiate DnDManager for environment such as Node since server side doesn't need DnD support
			response.dnDManager = this._isBrowserMode() ? new DnDManager() : null;
			response.stringService = new DashboardStringService();


			const lifeCycleFeature = {
				registerHandler : response.lifeCycleManager.registerLifeCycleHandler.bind(response.lifeCycleManager),
				invokeHandlers : response.lifeCycleManager.invokeLifeCycleHandlers.bind(response.lifeCycleManager)
			};

			const currProfile = this.internalDashboardAPI && this.internalDashboardAPI.getCurrentProfile();
			const enableProfileFeatureLoading = (currProfile !== undefined);


			this.featureLoader = new FeatureLoader({
				lifeCycleManager: lifeCycleFeature,
				featureNamePrefix: 'Dashboard',
				profileList: currProfile,
				enableProfileFeatureLoading: enableProfileFeatureLoading
			});

			this.featureLoader.registerFeature('Transaction', new Transaction(), CORE_PROFILE_SPEC);


			const contentFactory = new ContentFactory();
			this.featureLoader.registerFeature('ContentFactory', contentFactory, CORE_PROFILE_SPEC);

			this.featureLoader.registerFeature('BaseContentProvider', new BaseContentProvider({
				features: {
					API: this.dashboardAPI,
					ContentFactory: contentFactory
				}
			}), CORE_PROFILE_SPEC);

			this.featureLoader.registerFeature('PageContentProvider', new PagerContentProvider({
				features: {
					API: this.dashboardAPI,
					ContentFactory: contentFactory
				}
			}), CORE_PROFILE_SPEC);

			this.featureLoader.registerFeature('DashboardContentProvider', new DashboardContentProvider({
				features: {
					ContentFactory: contentFactory
				}
			}),
			CORE_PROFILE_SPEC
			);


			// Temporary -- needed by the live widget preview in order to create a content API
			// TODO - cleanup the live widget preview to be created without needing this.
			this.featureLoader.registerFeature('DashboardFeatureLoader.internal', {
				getAPI: () => this.featureLoader
			},
			CORE_PROFILE_SPEC, true);

			// Register bootstrap features.
			if (this.bootStrapFeatures){
				for (let name in this.bootStrapFeatures) {
					this.featureLoader.registerFeature(name, {
						getAPI: this.bootStrapFeatures[name].getAPI.bind(this.bootStrapFeatures[name])
					}, CORE_PROFILE_SPEC, true);
				}
			}

			// Some feature require the dashboard api (or the internal API)..
			// let us added so it can be requested through dependencies
			this.featureLoader.registerFeature('API', {
				getAPI: () => this.dashboardAPI
			}, CORE_PROFILE_SPEC, true);

			this.featureLoader.registerFeature('internal', {
				getAPI: () => this.internalDashboardAPI
			}, CORE_PROFILE_SPEC, true);

			this.featureLoader.registerFeature('FeatureRegistry.internal', {
				getAPI: () =>  this.featureLoader.getAPI()
			}, CORE_PROFILE_SPEC, true);

			this.featureLoader.registerFeature('LifeCycle', {
				getAPI: () => lifeCycleFeature
			}, CORE_PROFILE_SPEC, true);

			// Core dashboard services - can be called using getSvcSync
			this.services = new ServiceRegistry({
				services: {
					'Notification': {
						showError: () => {
							// todo - remove this
							// only needed by a code for the old focus view that is not longer used but causes an error if this is removed
							// we need to clean /dashboard-common/src/ui/WidgetBase.js - to stop calling getDashboardCoreSvc('Notification')
						}
					},
					'Logger': this.logger,
					'.LifeCycleManager': response.lifeCycleManager, // TODO : replace the usage of '.LifeCycleManager' with the featue 'LifeCycle'
					'.DndManager': response.dnDManager,
					'.StringResources': response.stringService
				}
			});


			// TODO - remove this services and cleanup the code that calls it
			this.services.biGlass = {
				glassContext: this.glassContext
			};

			response.serviceRegistry = this.services;
			response.featureLoader = this.featureLoader;

			return response;
		}

		_isBrowserMode(){
			return window !== undefined;
		}

		_registerServiceCollection(dashboardApi, collection, isFeature){
			var promises = [];
			if (collection){
				collection.forEach(function(serviceExtension) {
					if (!serviceExtension.name || !serviceExtension.class) {
						return;
					}
					promises.push(ClassFactory.loadModule(serviceExtension.class).then(function(ServiceExtension) {
						const options = serviceExtension.options || {};

						options.services = this.services;
						options.eventRouter = this.eventRouter;
						options.glassContext = this.glassContext;
						options.dashboardApi = dashboardApi;

						const extension = new ServiceExtension(options);

						let whenInitialized;

						if (isFeature && extension.initialize && typeof(extension.initialize) === 'function'){
							whenInitialized = extension.initialize();
						}else {
							whenInitialized = Promise.resolve();
						}

						return whenInitialized.then(() => {
							let extensionAPI = null;
							if (isFeature && extension.getAPI && typeof(extension.getAPI) === 'function'){
								extensionAPI = extension.getAPI();
							}

							this.services.register(serviceExtension.name, extensionAPI || extension );
							this._registerServiceHooks(extension);
						});

					}.bind(this)).catch(function(err) {
						//continue working even if an extension failed to load
						this.logger.error(err);
						// recover
						// TODO: maybe we don't need to recover.
						// Please check
					}.bind(this)));
				}.bind(this));
			}

			return Promise.all(promises);
		}

		/**
		 * creates the canvas and loads all the dashboard features
		 * TODO: remove the async step to initialize the ContentTypeRegistry
		 * @param {object} options
		 * @returns
		 */
		createRuntimeServices(options) {
			PerfUtils.createPerformanceMark({
				'component': 'dashboard',
				'name': 'createRuntimeServices',
				'state': 'start'
			});
			const dashboardApi = options.dashboardApi;

			let items =  [{
				name: 'ContentTypeRegistry',
				class: 'dashboard-core/js/features/dashboard/contentTypeRegistry/api/impl/ContentTypeRegistry',
				options: options.collections ? options.collections.contentTypes : [],
				dependencies: [],
				profiles: ['core']
			}];

			return this.featureLoader.loadFeaturesFromArray(items, dashboardApi.getFeature('internal').getBoardModel().getContentModel().features).then( ()=> {

				// Create the Canvas and make it available as a feature
				const response = this._createCanvas(dashboardApi, options.boardModel, options.widgetRegistry, options.extensions);
				this._registerCanvasFeature(response);


				return this.preDashboardInitialize().then(() => {
					let items = this._settings.get('com.ibm.bi.dashboard.features') || [];

					const isPropertiesV2Enabled = dashboardApi.getGlassCoreSvc('.FeatureChecker')
						.checkValue('dashboard', 'PropertiesV2', 'enabled');

					if (isPropertiesV2Enabled) {
						// PropertiesV2 - replace the old properties feature with the PropertiesV2
						// used for top level dashboard content and canvas content
						items = items.filter(item => item.id !== 'com.ibm.bi.dashboard.features.properties');
						items.push({
							containerId: 'com.ibm.bi.dashboard.features',
							name: 'Properties',
							id: 'com.ibm.bi.dashboard.features.properties',
							class: 'dashboard-core/js/features/common/properties/api/impl/PropertiesV2',
							dependencies: [ 'API'],
							profiles: ['core']
						});
					}

					items = items.concat(this.inlineFeatures);

					const PATH_PREFIX = 'dashboard-core/js/features/dashboard/';
					items.push({
						name: 'InternalPropertiesProvider',
						class: PATH_PREFIX + 'dashboardProperties/DashboardInternalPropertiesProvider',
						options: {
							templateCollection: options.collections ? options.collections.templates : []
						},
						dependencies: ['Properties', 'API'],
						profiles: ['core']
					});
					if(isPropertiesV2Enabled){
						items.push({
							name: 'InternalProperties',
							class: PATH_PREFIX + 'dashboardPropertiesV2/DashboardInternalPropertiesV2',
							dependencies: ['Properties', 'API', 'Canvas', 'Transaction'],
							profiles: ['authoring']
						});
					}
					return this.featureLoader.loadFeaturesFromArray(items, dashboardApi.getFeature('internal').getBoardModel().getContentModel().features).then(() => {
						return this.featureLoader.getFeature('Canvas.internal').initializeContentFeatures();
					});
				}).then(() => {
					return this._getServiceExtensions().then((servicesCollection) => {
						return this._getFeatureExtensions().then((featuresCollection) => {
							var promises = [];
							promises.push(this._registerServiceCollection(dashboardApi, servicesCollection, false));
							// TODO - removed once all features under "core-features" are moved to the correct "features" collection
							promises.push(this._registerServiceCollection(dashboardApi, featuresCollection, true));
							PerfUtils.createPerformanceMark({
								'component': 'dashboard',
								'name': 'createRuntimeServices',
								'state': 'end'
							});
							return Promise.all(promises);
						});
					});
				});
			});
		}

		/**
		 * Deprecated service collection. The new core-features collection must be used for new features.
		 */
		_getServiceExtensions() {
			return Promise.resolve(this._settings.get('com.ibm.bi.dashboard.serviceExtension'));
		}

		_getFeatureExtensions() {
			return Promise.resolve(deprecatedInlineFeatures.concat(this._settings.get('com.ibm.bi.dashboard.core-features') || []));
		}

		_registerServiceHooks(serviceExtension) {
			if (typeof(serviceExtension.getLifeCycleHandlers) === 'function') {
				var lifeCycleManager = this.services.getSvcSync('.LifeCycleManager');
				var handlers = serviceExtension.getLifeCycleHandlers();
				if (!Array.isArray(handlers)) {
					throw new Error('serviceExtension getLifeCycleHandlers method is expected to return an array');
				}
				handlers.forEach(function(handler) {
					if (!handler.name || !handler.action) {
						throw new Error('serviceExtension handlers are expected to have a name and action property');
					}
					lifeCycleManager.registerLifeCycleHandler(handler.name, handler.action, handler.priority, handler.triggerIfEventAlreadySent);
				});
			}
		}
	}
	return ServicesHelper;
});
